import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  LinkIcon,
  DocumentTextIcon,
  CalendarIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchSharedContent();
  }, [token]);

  const fetchSharedContent = async () => {
    try {
      const response = await fetch(`/api/v1/share/${token}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
        // Set first available content as active tab
        const files = Object.keys(result.data.content);
        if (files.length > 0) {
          setActiveTab(0);
        }
      } else {
        setError(result.error || "Failed to load shared content");
      }
    } catch (err) {
      setError("Failed to load shared content");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">
            Loading shared content...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Content Not Available
          </h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const files = Object.entries(data.content);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {data.title}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {data.description}
                </p>
              </div>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span>{new Date(data.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center">
                  <EyeIcon className="h-4 w-4 mr-1" />
                  <span>{data.viewCount} views</span>
                </div>
              </div>
            </div>

            {data.metadata?.url && (
              <div className="mt-3 flex items-center text-sm text-muted-foreground">
                <LinkIcon className="h-4 w-4 mr-1" />
                <span>Analysis for: {data.metadata.url}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {files.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-muted-foreground">No content available</p>
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow">
            {/* Tabs */}
            {files.length > 1 && (
              <div className="border-b">
                <nav className="flex -mb-px">
                  {files.map(([fileName], index) => (
                    <button
                      key={fileName}
                      onClick={() => setActiveTab(index)}
                      className={`
                        px-6 py-3 text-sm font-medium border-b-2 transition-colors
                        ${
                          activeTab === index
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-muted-foreground hover:text-card-foreground hover:border-border"
                        }
                      `}
                    >
                      {fileName.replace(".md", "").replace(/_/g, " ")}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {/* Content Display */}
            <div className="p-8">
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown>{files[activeTab][1]}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <span>Powered by</span>
            <a
              href="https://discoseo.com"
              target="_blank"
              rel="noopener noreferrer"
              alt="DiscoSEO"
              title="DiscoSEO — SEO and GEO action roadmaps"
              className="font-semibold text-blue-600 hover:text-blue-800"
            >
              DiscoSEO
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharedView;
